/**
 * Main Entry Point
 * ================
 * O'zgartirildi: React Router olib tashlandi, Modal yondashuvi qo'shildi.
 *
 * Endi sahifa navigate qilmaydi - modal 3D scene ustidan ochiladi.
 * Bu yondashuv ancha yengil va tez ishlaydi:
 * - 3D scene qayta yuklanmaydi
 * - Back button muammosi yo'q
 * - Sayt og'irligi kamaydi
 */

import { createRoot } from 'react-dom/client'
import { useState, useEffect, useRef } from 'react'
import { useProgress } from '@react-three/drei'
import Lenis from 'lenis'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import Scene from './Scene'
import Overlay from './Overlay/Overlay'
import ProjectModal from './components/ProjectModal'
import ParticlesCursor from './components/ParticlesCursor'
import { startGlobalAudio } from './Overlay/Nav'
import './loader.css'
import { Analytics } from "@vercel/analytics/react"


// Global Lenis instance - Nav'dan foydalanish uchun
export let globalLenis = null

// GSAP ScrollTrigger ni register qilish
gsap.registerPlugin(ScrollTrigger)

// Mobil browser uchun real viewport height
// CSS 100vh mobil browser'larda noto'g'ri ishlaydi
function setViewportHeight() {
  const svh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--svh', `${svh}px`);
}

// Dastlab o'rnatish
setViewportHeight();

// Resize va orientationchange da yangilash
window.addEventListener('resize', setViewportHeight);
window.addEventListener('orientationchange', () => {
  // Orientation o'zgarganda kichik delay kerak
  setTimeout(setViewportHeight, 100);
});

const root = createRoot(document.getElementById('root'))

/**
 * Custom Loader Component
 * 100% bo'lganda Enter button chiqadi
 * Button bosilganda ochiladi
 */
function CustomLoader({ onComplete }) {
  const { progress, active } = useProgress();
  const [show, setShow] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [finalProgress, setFinalProgress] = useState(0);

  // Progress orqaga qaytmaslik uchun - faqat yuqoriga boradi
  // 99% da to'xtaydi, 2s kutadi, keyin 100% bo'ladi
  const maxProgressRef = useRef(0);
  if (progress > maxProgressRef.current) {
    maxProgressRef.current = progress;
  }

  // 99% gacha ko'rsatish, 100% emas
  const cappedProgress = Math.min(maxProgressRef.current, 99);

  // Haqiqiy progress 100% bo'lganda 2s kutib keyin 100% ko'rsatish
  useEffect(() => {
    if (maxProgressRef.current >= 100 && finalProgress < 100) {
      const timer = setTimeout(() => {
        setFinalProgress(100);
      }, 2000); // 2 soniya kutish
      return () => clearTimeout(timer);
    }
  }, [progress, finalProgress]);

  // Display progress - 99% gacha yoki 100% (2s dan keyin)
  const displayProgress = finalProgress >= 100 ? 100 : cappedProgress;

  // Loader ko'rinayotganda scroll ni bloklash
  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
      // Wheel event ni ham bloklash
      const blockScroll = (e) => {
        e.preventDefault();
        e.stopPropagation();
      };
      document.addEventListener('wheel', blockScroll, { passive: false });
      document.addEventListener('touchmove', blockScroll, { passive: false });

      return () => {
        document.removeEventListener('wheel', blockScroll);
        document.removeEventListener('touchmove', blockScroll);
      };
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [show]);

  // 100% bo'lganda button ko'rsatish
  useEffect(() => {
    if (finalProgress >= 100) {
      // 100% bo'lganda tezda button ko'rsatish
      const timer = setTimeout(() => setIsReady(true), 100);
      return () => clearTimeout(timer);
    }
  }, [finalProgress]);

  // Enter button bosilganda
  const handleEnter = () => {
    // Audio ni ishga tushirish
    startGlobalAudio();

    setFadeOut(true);
    setTimeout(() => {
      setShow(false);
      onComplete?.();
    }, 800);
  };

  // Keyboard Enter ham ishlashi
  useEffect(() => {
    if (!isReady) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        handleEnter();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isReady]);

  if (!show) return null;

  return (
    <div className={`custom-loader ${fadeOut ? 'fade-out' : ''}`}>
      <div className="loader-content">
        {/* Animated lines background */}
        <div className="loader-lines">
          {[...Array(20)].map((_, i) => (
            <div key={i} className="loader-line" />
          ))}
        </div>

        {/* Progress section */}
        <div className="loader-progress">
          <div className="loader-bar">
            <div className="loader-fill" style={{ width: `${displayProgress}%` }} />
          </div>
          <span className="loader-percent">{Math.round(displayProgress)}%</span>
        </div>

        {/* Enter button - faqat 100% bo'lganda */}
        <button
          className={`loader-enter ${isReady ? 'visible' : ''}`}
          onClick={handleEnter}
        >
          <span className="enter-text">Enter</span>
          <span className="enter-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </span>
        </button>

        {/* Hint text */}
        <span className={`loader-hint ${isReady ? 'visible' : ''}`}>
          Press Enter or click to continue
        </span>
      </div>
    </div>
  );
}

/**
 * Lenis Smooth Scroll Hook
 * Lusion.co va 8bit.ai kabi professional smooth scroll
 * ScrollTrigger bilan sinxronlashtirilgan
 */
function useLenis(enabled = true) {
  const lenisRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;

    // Sekin scroll bo'ladigan section'lar (pin-spacer GSAP ScrollTrigger yaratadi)
    const slowSections = ['.section-5', '.section-6', '.pin-spacer'];
    // Tez scroll bo'ladigan section'lar (MyWorks gallery)
    const fastSections = ['.section-7', '.section-8', '.section-9', '.section-10', '.section-11', '.section-12', '.section-13', '.section-14', '.section-15'];
    const normalMultiplier = 1.2;     // Normal section'lar
    const slowMultiplier = 0.05;      // Sekin section'lar (5%)
    const fastMultiplier = 0.05;      // Tez section'lar (MyWorks gallery)

    const lenis = new Lenis({
      duration: 1.0,              // Tezroq response
      easing: (t) => 1 - Math.pow(1 - t, 2), // Quadratic - tezroq natural
      orientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: normalMultiplier,
      touchMultiplier: 2.5,
      infinite: false,
    });

    lenisRef.current = lenis;
    globalLenis = lenis;  // Global reference

    // MUHIM: Lenis ni ScrollTrigger bilan sinxronlashtirish
    // Bu scroll bug ni to'g'rilaydi
    lenis.on('scroll', ScrollTrigger.update);

    // gsap ticker bilan Lenis ni sinxronlashtirish
    gsap.ticker.add((time) => {
      lenis.raf(time * 1000);
    });

    // gsap ticker lagSmoothing ni o'chirish - aniqroq scroll uchun
    gsap.ticker.lagSmoothing(0);

    // Section elementlarini cache qilish - bir marta query, ko'p marta ishlatish
    let cachedSlowElements = null;
    let cacheTimeout = null;

    const getCachedSlowElements = () => {
      if (cachedSlowElements === null) {
        cachedSlowElements = [];
        for (const selector of slowSections) {
          const sections = document.querySelectorAll(selector);
          for (const section of sections) {
            cachedSlowElements.push(section);
          }
        }
        // 2 sekundda cache yangilanadi (DOM o'zgarishi uchun)
        clearTimeout(cacheTimeout);
        cacheTimeout = setTimeout(() => {
          cachedSlowElements = null;
        }, 2000);
      }
      return cachedSlowElements;
    };

    // Fast section elementlarini cache qilish
    let cachedFastElements = null;
    let fastCacheTimeout = null;

    const getCachedFastElements = () => {
      if (cachedFastElements === null) {
        cachedFastElements = [];
        for (const selector of fastSections) {
          const sections = document.querySelectorAll(selector);
          for (const section of sections) {
            cachedFastElements.push(section);
          }
        }
        clearTimeout(fastCacheTimeout);
        fastCacheTimeout = setTimeout(() => {
          cachedFastElements = null;
        }, 2000);
      }
      return cachedFastElements;
    };

    // Section tekshirish - cached elementlar bilan
    const isInSlowSection = () => {
      const elements = getCachedSlowElements();
      for (const section of elements) {
        const rect = section.getBoundingClientRect();
        // Viewport markazida bo'lsa
        if (rect.top < window.innerHeight * 0.7 && rect.bottom > window.innerHeight * 0.3) {
          return true;
        }
      }
      return false;
    };

    // Fast section tekshirish
    const isInFastSection = () => {
      const elements = getCachedFastElements();
      for (const section of elements) {
        const rect = section.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.7 && rect.bottom > window.innerHeight * 0.3) {
          return true;
        }
      }
      return false;
    };

    // Scroll event'da tezlikni dinamik o'zgartirish
    lenis.on('scroll', () => {
      if (isInSlowSection()) {
        lenis.options.wheelMultiplier = slowMultiplier;
        lenis.options.touchMultiplier = 0.5;
      } else if (isInFastSection()) {
        lenis.options.wheelMultiplier = fastMultiplier;
        lenis.options.touchMultiplier = 1.3;  // Tezroq touch scroll
      } else {
        lenis.options.wheelMultiplier = normalMultiplier;
        lenis.options.touchMultiplier = 2.5;
      }
    });

    // ScrollTrigger ni yangilash - sahifa to'liq yuklangandan keyin
    const refreshTimeout = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 100);

    // Resize da ScrollTrigger ni yangilash
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        ScrollTrigger.refresh();
      }, 200);
    };
    window.addEventListener('resize', handleResize);

    // ============================================
    // FOOTER TOUCH SCROLL FIX
    // Footer z-index: 100, canvas z-index: 1
    // Shuning uchun footer touch ustunligi bor
    // Canvas doim display: block va pointer-events: auto
    // ============================================
    // Bu yerda hech narsa qilish shart emas
    // CSS z-index yetarli

    // Cleanup
    return () => {
      clearTimeout(refreshTimeout);
      clearTimeout(resizeTimeout);
      clearTimeout(cacheTimeout);
      clearTimeout(fastCacheTimeout);
      cachedSlowElements = null;
      cachedFastElements = null;
      window.removeEventListener('resize', handleResize);
      gsap.ticker.remove(lenis.raf);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, [enabled]);

  return lenisRef;
}

/**
 * App Component
 * Modal state ni boshqaradi
 */
function App() {
  const [isLoaded, setIsLoaded] = useState(false);

  // Ochilgan proyekt ID si (null = modal yopiq)
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  // Loader Enter bosilganda
  const handleLoaderComplete = () => {
    setIsLoaded(true);
    // Loader yopilgandan keyin ScrollTrigger ni yangilash
    // Sahifa balandligi o'zgaradi, shuning uchun refresh kerak
    setTimeout(() => {
      ScrollTrigger.refresh();
    }, 500);
  };

  // Lenis faqat load bo'lgandan keyin ishlasin
  const lenisRef = useLenis(isLoaded);

  // Modal ochilganda Lenis ni to'xtatish
  useEffect(() => {
    if (lenisRef.current) {
      if (selectedProjectId) {
        lenisRef.current.stop();
      } else {
        lenisRef.current.start();
      }
    }
  }, [selectedProjectId, lenisRef]);

  // Proyekt tanlanganda modal ochish
  const handleProjectClick = (projectId) => {
    setSelectedProjectId(projectId);
  };

  // Modal yopish
  const handleCloseModal = () => {
    setSelectedProjectId(null);
    // Modal yopilgandan keyin ScrollTrigger ni yangilash
    setTimeout(() => {
      ScrollTrigger.refresh();
    }, 100);
  };

  return (
    <>
      {/* Analytics */}
      <Analytics />

      {/* 3D Scene - onProjectClick prop bilan */}
      <Scene onProjectClick={handleProjectClick} />

      {/* HTML Overlay */}
      <Overlay />

      {/* Particles Cursor - soju22 CodePen stilida */}
      <ParticlesCursor />

      {/* Custom Loader */}
      <CustomLoader onComplete={handleLoaderComplete} />

      {/* Project Modal - faqat selectedProjectId mavjud bo'lganda ko'rinadi */}
      <ProjectModal
        projectId={selectedProjectId}
        onClose={handleCloseModal}
      />
    </>
  )
}

root.render(<App />)

